import { Controller } from "@hotwired/stimulus";
import { CsrfToken } from "../shared/csrf_token";
import { TurboStreamHeaders } from "../shared/turbo_stream_headers";

// Connects to data-controller="resumin--email-autocomplete"
export default class extends Controller {
  static targets = ["input"];
  static values = {
    delay: { type: Number, default: 500 },
    formName: { type: String },
  };

  connect() {
    CsrfToken(this);
    TurboStreamHeaders(this);
    this.timeoutId = null;
    this.selectedIndex = -1;
    this.hasSuggestions = false;

    // イベントリスナーを追加
    this._boundHandleGlobalKeydown = this._handleGlobalKeydown.bind(this);
    document.addEventListener("keydown", this._boundHandleGlobalKeydown, true);
  }

  disconnect() {
    this._clearExistingTimeout();
    // イベントリスナーを削除
    document.removeEventListener(
      "keydown",
      this._boundHandleGlobalKeydown,
      true
    );
  }

  search(e) {
    e.preventDefault();
    this._clearExistingTimeout();

    this.timeoutId = setTimeout(() => {
      const text = this._extractAfterAtSymbol(e.target.value);
      if (text.length === 0) return;

      this._requestEmailAutocomplete(text);
      this.timeoutId = null;
    }, this.delayValue);
  }

  fill(e) {
    const [local] = this.inputTarget.value.split("@");
    const domain = e.target.dataset.domain;
    this.inputTarget.value = `${local}@${domain}`;
    this.removeSuggestion();
  }

  removeSuggestion() {
    const suggestion = this.element.querySelector("#domain-suggestions");
    if (suggestion) {
      suggestion.remove();
    }
    this.selectedIndex = -1;
    this.hasSuggestions = false;
  }

  // グローバルなキーダウンイベントハンドラー
  _handleGlobalKeydown(e) {
    // このコントローラーにフォーカスがある場合のみ処理
    if (!this.inputTarget.matches(":focus")) return;

    const suggestions = this.element.querySelector("#domain-suggestions");
    if (!suggestions) return;

    const items = suggestions.querySelectorAll("li");
    if (items.length === 0) return;

    this.hasSuggestions = true;

    // キー操作の処理
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.selectedIndex = this.selectedIndex + 1;
        // 最後の項目を超えたら最初に戻る
        if (this.selectedIndex >= items.length) {
          this.selectedIndex = 0;
        }
        this._highlightItem(items);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.selectedIndex = this.selectedIndex - 1;
        // 最初の項目より前に行ったら最後の項目に移動
        if (this.selectedIndex < 0) {
          this.selectedIndex = items.length - 1;
        }
        this._highlightItem(items);
        break;
      case "Enter":
        if (
          this.hasSuggestions &&
          this.selectedIndex >= 0 &&
          this.selectedIndex < items.length
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          // ドメインを取得して手動で入力フィールドに設定
          const domain = items[this.selectedIndex].dataset.domain;
          const [local] = this.inputTarget.value.split("@");
          this.inputTarget.value = `${local}@${domain}`;
          this.removeSuggestion();
          // prevent_enter_controllerがバインドされている場合でも動作するように
          // inputイベントを手動で発火させる
          this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
          return false;
        }
        break;
      case "Escape":
        e.preventDefault();
        this.removeSuggestion();
        break;
    }
  }

  // キーボードイベントのハンドラー（ビューから呼び出される）
  handleKeydown(e) {
    // Enterキーの場合は、サジェストリストがあれば処理
    if (e.key === "Enter") {
      const suggestions = this.element.querySelector("#domain-suggestions");
      if (suggestions) {
        const items = suggestions.querySelectorAll("li");
        if (
          items.length > 0 &&
          this.selectedIndex >= 0 &&
          this.selectedIndex < items.length
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          // ドメインを取得して手動で入力フィールドに設定
          const domain = items[this.selectedIndex].dataset.domain;
          const [local] = this.inputTarget.value.split("@");
          this.inputTarget.value = `${local}@${domain}`;
          this.removeSuggestion();
          // prevent_enter_controllerがバインドされている場合でも動作するように
          // inputイベントを手動で発火させる
          this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
          return false;
        }
      }
    }
  }

  _highlightItem(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("bg-slate-200");
      } else {
        item.classList.remove("bg-slate-200");
      }
    });
  }

  _extractAfterAtSymbol(input) {
    const str = input.trim();
    const atPos = str.indexOf("@");
    return atPos >= 0 ? str.slice(atPos + 1) : "";
  }

  _requestEmailAutocomplete(text) {
    fetch("/email_autocomplete", {
      method: "POST",
      body: new URLSearchParams({
        email_domain: text,
        form_name: this.formNameValue,
      }),
      headers: {
        ...this.csrfToken(),
        ...this.turboStreamHeaders(),
      },
    })
      .then((response) => {
        const ok = response.ok;
        const status = response.status;
        if (!ok && status !== 422) {
          throw new Error("Network response was not ok");
        }

        return response.text().then((html) => ({ status, ok, html }));
      })
      .then(({ status, ok, html }) => {
        Turbo.renderStreamMessage(html);
        this.selectedIndex = -1;
        // サジェストリストが表示されたかチェック
        setTimeout(() => {
          const suggestions = this.element.querySelector("#domain-suggestions");
          this.hasSuggestions = !!suggestions;
        }, 100);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      });
  }

  _clearExistingTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
